'use client'

import * as React from 'react'
import type { CwflowMountOptions } from './entry'
import { pickWidgetLocale, WIDGET_I18N, type WidgetLocale } from './i18n'

/**
 * Drop-in cwFLOW widget — minimal "fuzzy search + redirect" pill bar.
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ <input placeholder>           [ buttonText           ] │  ← inside one pill
 *   └────────────────────────────────────────────────────────┘
 *      ▼ (results dropdown when typing)
 *
 * Operator feedback on the previous version: "sieht einfach nicht gut
 * aus, … sonst soll da nichts sein, wir haben wieder zu viel drumherum."
 *
 * Hard rules:
 *   - NO logo, NO title, NO subtitle, NO hint paragraph rendered by us
 *   - Container background is transparent — caller drops the widget on
 *     whatever background colour they want (the screenshot showed a
 *     yellow brand wrapper; we don't fight that)
 *   - Pill bar is white (input area) + dark brand pill (submit button)
 *     on the right, attached. Same rounded shape as `.btn-primary`.
 *   - Button text configurable via `options.buttonText`. Placeholder via
 *     `options.placeholder`. Both have sane defaults.
 *
 * The redirect logic + prefill-handshake into /embed/<slug>?prefill_*=…
 * stays unchanged from the previous iteration — that part works.
 */

interface WidgetCheckoutProps {
  apiBase: string
  options: CwflowMountOptions
}

interface FuzzyMatch {
  address_id?: string
  plz: string
  city: string
  street: string
  house_number_range?: string
  property_id?: string
}

interface TenantBoot {
  tenant?: { slug?: string; display_name?: string; name?: string }
  branding?: {
    primary_color?: string
    secondary_color?: string
  }
  cwflow_primary_host?: string
}

type Status = 'idle' | 'loading' | 'no_results' | 'error'

export function WidgetCheckout({ apiBase, options }: WidgetCheckoutProps) {
  const [tenantBoot, setTenantBoot] = React.useState<TenantBoot | null>(null)
  const [query, setQuery] = React.useState('')
  const [matches, setMatches] = React.useState<FuzzyMatch[]>([])
  const [status, setStatus] = React.useState<Status>('idle')
  const [highlight, setHighlight] = React.useState(0)
  const [picked, setPicked] = React.useState<FuzzyMatch | null>(null)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const wrapRef = React.useRef<HTMLDivElement | null>(null)
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // i18n — the active locale drives the bundled defaults (button label,
  // placeholder, error / no-results messages). Operator-supplied
  // `options.buttonText` / `options.placeholder` still win — those are
  // explicit overrides for hosts who want their own copy regardless of
  // language. Submit + redirect carries the same locale forward via
  // `?lang=<locale>` so the embed page opens in sync with the homepage.
  const locale: WidgetLocale = pickWidgetLocale(options.locale)
  const t = WIDGET_I18N[locale]
  const placeholder = options.placeholder?.trim() || t.placeholder
  const buttonText = options.buttonText?.trim() || t.buttonText

  // ─── Tenant boot — only for primary_host (redirect target) ───────────
  React.useEffect(() => {
    let cancelled = false
    fetch(`${apiBase}/api/v1/public/tenants/${encodeURIComponent(options.tenant)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return
        const data: TenantBoot | null = j?.data || j
        if (data) setTenantBoot(data)
      })
      .catch(() => {
        /* widget can run without — degrades gracefully */
      })
    return () => {
      cancelled = true
    }
  }, [apiBase, options.tenant])

  // ─── Fuzzy search on every keystroke (200ms debounce) ────────────────
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setMatches([])
      setStatus('idle')
      return
    }
    setStatus('loading')
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${apiBase}/api/v1/public/address-fuzzy`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ tenantSlug: options.tenant, query: trimmed, limit: 12 }),
        })
        if (!r.ok) {
          setStatus('error')
          return
        }
        const j = await r.json()
        const list: FuzzyMatch[] = j?.data?.matches || j?.matches || []
        setMatches(list)
        setHighlight(0)
        setStatus(list.length === 0 ? 'no_results' : 'idle')
      } catch (e) {
        setStatus('error')
        options.onError?.(e instanceof Error ? e : new Error('fuzzy_failed'))
      }
    }, 200)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, apiBase, options])

  // Close dropdown on outside click — standard pattern.
  React.useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  function pick(m: FuzzyMatch) {
    setPicked(m)
    setMatches([])
    setOpen(false)
    setQuery(formatMatch(m))
    inputRef.current?.blur()
  }

  async function submit() {
    setError(null)
    if (!picked) {
      // Auto-pick if there's exactly one obvious match.
      if (matches.length === 1) {
        pick(matches[0])
        // Defer to next tick so state has settled before we redirect.
        setTimeout(submit, 0)
        return
      }
      setError(t.errPickAddress)
      return
    }
    setSubmitting(true)
    try {
      const slug = options.tenant.toLowerCase()
      const host =
        tenantBoot?.cwflow_primary_host || `${slug}.flow.app.cwerke.net`
      const params = new URLSearchParams({
        prefill_plz: picked.plz,
        prefill_city: picked.city,
        prefill_street: picked.street,
        prefill_house_number: picked.house_number_range || '',
        ...(picked.address_id ? { prefill_address_id: picked.address_id } : {}),
        source: 'widget',
        // Carry the active widget locale into the embed page so the
        // FLOW Bestellstrecke opens in the same language as the host
        // page. cwFLOW reads `?lang=` in I18nProvider; unknown values
        // fall back to the tenant default-locale silently.
        lang: locale,
        ...(options.brand ? { brand: options.brand } : {}),
        ...(options.channel ? { channel: options.channel } : {}),
      })
      window.location.href = `https://${host}/embed/${slug}?${params.toString()}`
    } catch (e) {
      setError(e instanceof Error ? e.message : t.errRedirect)
      options.onError?.(e instanceof Error ? e : new Error('submit_failed'))
    } finally {
      setSubmitting(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, matches.length - 1))
      setOpen(true)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (matches[highlight]) pick(matches[highlight])
      else if (picked) submit()
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="cw-widget-bare" ref={wrapRef}>
      <div className="cw-widget-bar" data-state={status}>
        <input
          ref={inputRef}
          className="cw-widget-bar-input"
          type="text"
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            setQuery(e.target.value)
            setPicked(null)
            setError(null)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label={t.ariaSearchLabel}
          data-testid="cw-widget-fuzzy"
        />
        {status === 'loading' && (
          <span className="cw-widget-bar-spinner" aria-hidden="true" />
        )}
        <button
          type="button"
          className="cw-widget-bar-btn"
          onClick={submit}
          disabled={submitting || (!picked && matches.length !== 1)}
          data-testid="cw-widget-submit"
        >
          {submitting ? t.buttonSubmitting : buttonText}
        </button>
      </div>

      {/* Results dropdown — floats below the bar. Has its own rounded
          card so the visual continues even though the bar+dropdown are
          two separate stacked rectangles. */}
      {open && matches.length > 0 && (
        <ul className="cw-widget-bar-results" role="listbox">
          {matches.map((m, i) => (
            <li
              key={`${m.plz}-${m.city}-${m.street}-${m.house_number_range}-${i}`}
              role="option"
              aria-selected={i === highlight}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(m)}
              data-testid={`cw-widget-match-${i}`}
            >
              <div className="cw-widget-match-line">
                {m.street}
                {m.house_number_range ? ` ${m.house_number_range}` : ''}
              </div>
              <div className="cw-widget-match-sub">
                {m.plz} {m.city}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* No-results / error pills — only show when actively typed something
          the user is waiting on. Don't show "Keine Treffer" before they
          type a real query (UX of empty input shouldn't be a warning). */}
      {open && status === 'no_results' && query.trim().length >= 2 && (
        <div className="cw-widget-bar-empty">{t.noResults}</div>
      )}
      {error && <div className="cw-widget-bar-error">{error}</div>}
    </div>
  )
}

function formatMatch(m: FuzzyMatch): string {
  const parts: string[] = []
  if (m.plz) parts.push(m.plz)
  if (m.city) parts.push(m.city + ',')
  if (m.street) parts.push(m.street)
  if (m.house_number_range) parts.push(m.house_number_range)
  return parts.join(' ').replace(' ,', ',').trim()
}
